/**
 * bakeStatusStore — module-level singleton tracking per-environment IBL bake
 * status (ENG-561). Mirrors the graphState/liveTuneStore pattern: a plain
 * subscribe/notify store, never React state on a hot path.
 *
 * Keyed by environment asset id. Polls the server (via bakeLightingApi) only
 * while a bake is in flight, so an idle app makes no requests.
 */
import {
  fetchBakeStatus, requestBake, STUB, _stubAdvance,
} from './bakeLightingApi'
import { getCurrentCharacterId } from './characterState'
import { bumpBakeGeneration } from './bakeReload'
import { forgetEnvironmentFilesById } from './environmentFiles'

let state = {} // assetId -> { status, progress, error, startedAt, slug, name }
const listeners = new Set()
const timers = {}

// Completion/failure events, emitted regardless of what env/character is
// currently selected — so a bake that finishes after the user navigates away
// still notifies them (global toast). Distinct from `listeners`, which drive
// the always-mounted chip.
const eventListeners = new Set()

function emit() {
  if (typeof window !== 'undefined') window.__BAKE_STATUS__ = state
  for (const fn of listeners) fn()
}

export function subscribeBake(fn) { listeners.add(fn); return () => listeners.delete(fn) }
export function getBakeState(assetId) { return (assetId && state[assetId]) || null }

/** Subscribe to one-shot bake lifecycle events: { assetId, name, kind }. */
export function subscribeBakeEvents(fn) { eventListeners.add(fn); return () => eventListeners.delete(fn) }
function emitBakeEvent(evt) { for (const fn of eventListeners) fn(evt) }

async function refresh(assetId) {
  const prev = state[assetId]
  try {
    // Look the asset up under its OWN character so polling survives a switch.
    const s = await fetchBakeStatus(assetId, prev?.slug)
    // Preserve/seed startedAt so the chip can show an honest elapsed timer.
    // (The server reports no mid-bake progress, so elapsed time is the only
    // truthful in-progress signal — see ENG-561 bake UX.)
    let startedAt = prev?.startedAt ?? null
    if (s.status === 'baking' && !startedAt) startedAt = Date.now()
    if (s.status !== 'baking') startedAt = null
    const next = { ...prev, ...s, startedAt }
    state = { ...state, [assetId]: next }
    // baking -> ready/error transition: notify globally (works even if the
    // user has navigated to another env/character since starting the bake).
    // `what` names the artifact this bake produced ('lighting' | 'navmesh',
    // recorded by startBake) so the toast can say which build failed — a
    // navmesh error announced as "Lighting bake failed" sends the user
    // debugging the wrong feature (it did).
    if (prev?.status === 'baking' && s.status !== 'baking') {
      emitBakeEvent({
        assetId, name: next.name || prev?.name || 'environment', kind: s.status,
        what: next.kind || 'lighting',
      })
      // A completed bake wrote new baked artifacts (navmesh.json, lightprobes,
      // reflections, …) beside the .ply. The server-stated `files` map cached
      // for this env predates them, so drop it FIRST — the re-fetch the bump
      // triggers must see the post-bake list, or the volumes re-check against
      // a map with no "navmesh" entry and the row keeps reading NOT BUILT
      // (observed live: the reload fired and still missed the artifact).
      // Only on success: an errored bake produced no new files to reload.
      if (s.status === 'ready') {
        forgetEnvironmentFilesById(assetId)
        bumpBakeGeneration(assetId)
      }
    }
    emit()
    if (s.status === 'baking') startPoll(assetId)
  } catch (e) {
    // Loud failure (CLAUDE.md): surface error, never a fake "ready".
    state = { ...state, [assetId]: { status: 'error', error: String(e?.message ?? e) } }
    emit()
  }
}

/** Load status once for an asset if we don't have it yet. */
export function ensureBakeStatus(assetId) {
  if (!assetId || state[assetId]) return
  refresh(assetId)
}

function startPoll(assetId) {
  if (timers[assetId]) return
  timers[assetId] = setInterval(async () => {
    if (STUB) _stubAdvance(assetId)
    await refresh(assetId)
    const s = state[assetId]
    if (!s || s.status !== 'baking') { clearInterval(timers[assetId]); delete timers[assetId] }
  }, STUB ? 700 : 2500)
}

/**
 * Trigger a bake for an environment asset with the chosen params, then poll
 * status so the chip reflects it. `requester` is the API call (defaults to the
 * lighting bake); the navmesh panel passes `requestNavBake`. Both hit the same
 * unified bake job, so either flips bakeStatus → baking and drives the chip.
 */
export async function startBake(assetId, params, requester = requestBake, kind = 'lighting') {
  if (!assetId) return
  // Capture the env's character now (it's the active one at trigger time) so
  // subsequent polls query the right list even after a character switch.
  const slug = getCurrentCharacterId()
  try {
    const s = await requester(assetId, params)
    // `kind` ('lighting' | 'navmesh') records WHICH artifact this bake produces
    // so the chip can show the spinner on just that row (ENG-592 separation).
    state = { ...state, [assetId]: { ...state[assetId], ...s, slug, kind, startedAt: Date.now() } }
    emit()
    startPoll(assetId)
  } catch (e) {
    state = { ...state, [assetId]: { status: 'error', error: String(e?.message ?? e) } }
    emit()
  }
}
